import React, { forwardRef } from "react";
import { useDebouncedCallback } from "use-debounce";
import { TextFieldProps } from "./types";
import { classNames } from "helpers";
import { ReactSVG } from "react-svg";
import Tooltip from "ui/components/Tooltip/Tooltip";

const TYPING_DEBOUNCE_TIME = 500;

const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      id,
      type,
      value,
      onChange,
      name,
      label,
      placeholder,
      readOnly,
      disableDebounce = false,
      debounceTime,
      disabled,
      disableAutoFocus = false,
      required,
      error,
      forceShowError,
      startIcon,
      endIcon,
      width,
      height = "normal",
      cssClassName,
      onClick,
      groupAppend,
      groupPrepend,
      autoComplete = "off",
      maxLength,
      ...otherProps
    },
    ref
  ) => {

    const canEdit = true;

    const isDisabled = disabled || !canEdit;

    const hasValue = value !== undefined && value !== null && value !== "";
    const isError = !!error && (forceShowError || !hasValue);


    React.useEffect(() => {
      setLastEvent({
        target: { value: value === null ? "" : value },
      } as any);
    }, [value]);

    const [lastEvent, setLastEvent] = React.useState<
      React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
    >({
      target: { value: value === null ? "" : value },
    } as any);

    const denouncedOnChange = useDebouncedCallback(() => {
      if (onChange) {
        onChange(lastEvent);
      }
    }, debounceTime ?? TYPING_DEBOUNCE_TIME);

    const handleOnChanged = (
      e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
    ) => {
      e.persist();
      setLastEvent(e);
      denouncedOnChange();
    };

    const handleOnBlur = () => {
      denouncedOnChange.flush();
    };

    const handleOnFocus = (
      e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      e.target.select();
    };

    const widthClassName = () => {
      switch (width) {
        case "min":
          return "w-24";
        case "small":
          return "w-48";
        case "medium":
          return "w-96";
        case "large":
          return "w-full";
        case "full":
          return "w-full";
        default:
          return "w-full";
      }
    };

    const heightClassName = () => {
      switch (height) {
        case "small":
          return "py-1.5";
        case "normal":
          return "py-2.5";
        case "big":
          return "py-3.5";
        default:
          return "py-2.5";
      }
    };

    const requiredClassName = () => {
      if (isDisabled) {
        return "ring-1 ring-slate-300 bg-gray-100";
      }
      /*
        Obrub prati grešku, ne `required`. Polje koje nije obavezno može imati grešku
        (neispravan format, ili pravilo koje gleda dva polja zajedno), a dotad je
        dobivalo samo ikonu bez ijednog znaka na samom polju.
      */
      if (isError) {
        // `focus:!ring-red-500` jer bi inače focus ring iz commonClassName pregazio crveni obrub.
        return "ring-1 ring-red-500 bg-red-50 text-red-900 placeholder-red-700 focus:!ring-red-500";
      }
      return "ring-1 ring-slate-300 bg-slate-50";
    };


    const commonClassName = () => {
      return classNames("relative cursor-default rounded-sm text-left text-gray-800 placeholder:text-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-slate-400 sm:text-sm sm:leading-6 z-0 disabled:cursor-default disabled:text-gray-900 disabled:border-gray-300 disabled:shadow-none", heightClassName());
    };

    return (
      <>
        {label && (
          <label className="block text-sm mb-2 font-medium leading-6 text-[#344054]">
            {label}
            {required && <span className="text-red-500"> *</span>}
          </label>
        )}
        <div className="flex rounded-sm relative ">
          {groupPrepend && (
            <div className="absolute top-2/4 -translate-y-1/2 left-2 z-0 text-sm text-gray-500">
              {groupPrepend}
            </div>
          )}
          {startIcon && (
            <span className="absolute top-2/4 -translate-y-1/2 left-2 z-10 block">
              <ReactSVG src={startIcon} className="w-4 h-4 stroke-[#667085]" />
            </span>
          )}
          <input
            ref={ref}
            id={id}
            type={type}
            value={
              disableDebounce
                ? value === null
                  ? ""
                  : value
                : lastEvent.target.value
            }
            name={name}
            disabled={isDisabled}
            readOnly={readOnly}
            onChange={disableDebounce ? onChange : handleOnChanged}
            onBlur={disableDebounce ? undefined : handleOnBlur}
            onFocus={disableAutoFocus ? undefined : handleOnFocus}
            onClick={onClick}
            className={classNames(
              startIcon ? "pl-8" : "pl-3",
              endIcon ? "pr-8" : "pr-3",
              commonClassName(),
              widthClassName(),
              requiredClassName(),
              cssClassName
            )}
            autoComplete={autoComplete}
            placeholder={placeholder}
            required
            maxLength={maxLength}
            data-label={label}
            {...otherProps}
          />
          {endIcon && (
            <span className="absolute top-2/4 -translate-y-1/2 right-2 z-10 block pointer-events-none">
              <ReactSVG src={endIcon} className="w-4 h-4 stroke-[#667085]" />
            </span>
          )}
          {groupAppend && (
            <div className="absolute right-2 top-2/4 -translate-y-1/2 z-10 text-sm  text-gray-500">
              {groupAppend}
            </div>
          )}
          {isError && (
            <div className="absolute right-2 top-2/4 -translate-y-1/2 z-10">
              {/* Ikona je uz desni rub polja, pa se tooltip otvara ulijevo da ne izađe iz ekrana. */}
              <Tooltip description={error} position="top-left" />
            </div>
          )}

        </div>
      </>
    );
  }
);

export default React.memo(TextField);
